import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { adGroupMetricCategoriesFactory } from '@bingads-webui/custom-columns-shared-config';
import {
  BidStrategyNameColumn,
  ColumnConversions,
  ColumnViewThroughConversions,
  DeliveryColumn,
  ColumnTopImpressionRate,
  ColumnAbsoluteTopImpressionRate,
  ColumnConversionsCredit,
  ColumnViewThroughConversionsCredit,
} from '@bingads-webui-campaign/campaignui-common-columns';
import { RSAAdCustomizerAttributeColumn } from '@bingads-webui-campaign/rsa-ad-customizer-attribute-column';
import { LabelsColumn } from '@bingads-webui-campaign/labels';
import { ColumnsConfigBuilder } from '@bingads-webui-campaign/summary-grid-helper';
import { AdGroupCashbackPercentColumn } from './attributes/ad-group-cashback-percent-column';
import { AdGroupCashbackScopeColumn } from './attributes/ad-group-cashback-scope-column';
import { AdGroupCouponColumn } from './attributes/ad-group-coupon-column';
import { LanguageColumn } from './attributes/language';
import { AdGroupColumn } from './attributes/ad-group';
import { CampaignColumn } from './attributes/campaign';
import { BidStrategyTypeColumn } from './attributes/bid-strategy-type';
import { SearchBidColumn } from './attributes/search-bid-column';
import { CpmBidColumn } from './attributes/cpm-bid-column';
import { CpvBidColumn } from './attributes/cpv-bid-column';
import { PercentCpcBidColumn } from './attributes/percent-cpc-bid-column';
import { CommissionRateColumn } from './attributes/pay-per-stay-bid-column';
import { QualityScoreColumn } from './attributes/quality-score';
import { MediumColumn } from './attributes/medium';
import { AdGroupIdColumn } from './attributes/ad-group-id';
import { StartDateColumn } from './attributes/start-date';
import { EndDateColumn } from './attributes/end-date';
import { StatusIconColumn } from './attributes/status-icon';
import { AdGroupTypeColumn } from './attributes/ad-group-type';
import { CustomParametersColumn, FinalUrlSuffixColumn, TrackingTemplateColumn } from './attributes/tracking-template-columns';
import {
  getLockedBidColumns, getUnlockedBidColumns, isBiddingSchemeUpdated,
  getSelectedBidColumns, columnProcessorBeforeSetRecordGetter
} from './dynamic-column-config-utils';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-groups-tab');
const i18nErrors = globalContext.require('component/api-errors-campaign/index');
const { Columns, SelectedColumns } = globalContext.require('component/bingads-grid/service/column-chooser-preferences');
const { LegacyColumns, LegacySelectedColumns } = globalContext.require('component/bingads-grid/service/legacy-column-chooser-preferences');
const { CustomColumns } = globalContext.require('component/bingads-grid/service/custom-column-preferences');
const config = globalContext.require('component/config/index');
const { LegacyGridFlag } = globalContext.require('component/bingads-grid/service/legacy-grid-flag-preferences');
const jsData = globalContext.require('component/js-data-campaign/index');
const Permissions = config.get('Permissions');
let builder = null;
let campaignIdInLastBuilder;
let lastGlobalCampaignTypeFilter;

const {
  IsViewThroughConversionEnabled,
  IsAudienceSOVEnabled,
  IsDSAMixedModeCampaignEnabled,
  IsAvgPositionDeprecationPhase1Enabled,
  IsAvgPositionDeprecationPhase2Enabled,
  IsPersonalizedOffersEnabled,
  IsPersonalizedOffersV3Enabled,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsViewThroughConversionsRevenueEnabled,
  IsPersonalizedOffersV4Enabled,
  dynamic = {},
} = Permissions;

const {
  PartialConversions: IsPartialConversionsEnabled,
  ColumnConversion: IsColumnConversionEnabled,
  HotelCampaign: IsHotelCampaignEnabled,
  XandrVideoCampaigns,
  CPSReporting,
  InstallPerformanceColumns,
  ConvValueRules: isConversionValueRuleColumnsEnabled,
} = dynamic;

const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

const isPortfolioBidStrategyEnabled = _.result(Permissions.dynamic, 'PortfolioBidStrategy');

const allBidColumns = [
  SearchBidColumn,
  CpvBidColumn,
  CpmBidColumn,
  PercentCpcBidColumn,
  CommissionRateColumn,
];

function getBuilder({ campaignId, currentCampaign, globalCampaignTypeFilter }) {
  // initialize column config if
  //   1: not initialize yet.
  //   2: scope has changed from account to campaign level or campaign to account level
  //   3: global campaign filter updates
  if (builder === null || campaignId !== campaignIdInLastBuilder ||
    lastGlobalCampaignTypeFilter !== globalCampaignTypeFilter ||
    isBiddingSchemeUpdated({ campaignId, currentCampaign })) {
    campaignIdInLastBuilder = campaignId;
    lastGlobalCampaignTypeFilter = globalCampaignTypeFilter;
    let lockedColumns = [
      AdGroupColumn,
    ];

    if (!campaignId) {
      lockedColumns.push(CampaignColumn);
    }

    lockedColumns = lockedColumns.concat(getLockedBidColumns({
      campaignId,
      currentCampaign,
      allBidColumns,
      IsVideoAdsForCampaignEnabled: IsVideoAdsForCampaignEnabled || XandrVideoCampaigns,
      IsManualCpmEnabled,
      IsHotelCampaignEnabled,
      config,
      globalCampaignTypeFilter,
    }));

    builder = new ColumnsConfigBuilder({ Permissions })
      .addCategory('Attributes', category => category.addColumns(_.compact(_.union(lockedColumns, [
        DeliveryColumn,
        isPortfolioBidStrategyEnabled && BidStrategyNameColumn,
        BidStrategyTypeColumn,
        ...getUnlockedBidColumns({
          campaignId,
          currentCampaign,
          allBidColumns,
          IsVideoAdsForCampaignEnabled: IsVideoAdsForCampaignEnabled || XandrVideoCampaigns,
          IsManualCpmEnabled,
          IsHotelCampaignEnabled,
          config,
          globalCampaignTypeFilter,
        }),
        QualityScoreColumn,
        MediumColumn,
        AdGroupIdColumn,
        StartDateColumn,
        EndDateColumn,
        Permissions.IsCampaignMultipleLanguageEnabled && LanguageColumn,
        TrackingTemplateColumn,
        CustomParametersColumn,
        FinalUrlSuffixColumn,
        LabelsColumn,
        IsPersonalizedOffersEnabled && !IsPersonalizedOffersV4Enabled
          ? AdGroupCashbackPercentColumn : null,
        IsPersonalizedOffersEnabled && !IsPersonalizedOffersV4Enabled
          ? AdGroupCashbackScopeColumn : null,
        IsPersonalizedOffersV3Enabled && !IsPersonalizedOffersV4Enabled
          ? AdGroupCouponColumn : null,
        IsDSAMixedModeCampaignEnabled && AdGroupTypeColumn,
      ]))))
      .addPerformanceCategoryForAdGroups({
        IsAvgPositionDeprecationPhase2Enabled,
      })
      .addConversionsCategory({
        IsViewThroughConversionEnabled,
        IsViewThroughConversionsRevenueEnabled,
        withPartialConversions: IsPartialConversionsEnabled,
        IsColumnConversionEnabled,
        isCPSEnabled: CPSReporting,
        isInstallColumnsEnabled: InstallPerformanceColumns,
        isConversionValueRuleColumnsEnabled,
      })
      .addCompetitiveCategoryForCampaignOrAdGroups({
        IsAudienceSOVEnabled,
      });

    if (Permissions.IsCallMeteringAllowed) {
      builder.addCallDetailsCategory();
    }

    builder
      .setLockedColumns([
        StatusIconColumn,
        ...lockedColumns,
      ])
      .setSelectedColumns(_.compact([
        IsDSAMixedModeCampaignEnabled && AdGroupTypeColumn,
        DeliveryColumn,
        isPortfolioBidStrategyEnabled && BidStrategyNameColumn,
        BidStrategyTypeColumn,
        ...getSelectedBidColumns({
          allBidColumns,
          IsVideoAdsForCampaignEnabled: IsVideoAdsForCampaignEnabled || XandrVideoCampaigns,
          IsManualCpmEnabled,
        }),
        LabelsColumn,
        QualityScoreColumn,
        MediumColumn,
        AdGroupIdColumn,
        StartDateColumn,
        EndDateColumn,
        Permissions.IsCampaignMultipleLanguageEnabled && LanguageColumn,
        TrackingTemplateColumn,
        CustomParametersColumn,
        FinalUrlSuffixColumn,
        usePartialConversions && ColumnConversionsCredit,
        !usePartialConversions && ColumnConversions,
        usePartialConversions && ColumnViewThroughConversionsCredit,
        !usePartialConversions && ColumnViewThroughConversions,
        IsAvgPositionDeprecationPhase1Enabled && ColumnTopImpressionRate,
        IsAvgPositionDeprecationPhase1Enabled && ColumnAbsoluteTopImpressionRate,
      ]));
  }

  return builder;
}

const nameFromLegacy = {
  StatusIcon: 'Status',
  Name: 'AdGroup',
  CampaignName: 'Campaign',
  DeliveryStatusView: 'Delivery',
  DisplayBiddingStrategyType: 'BidStrategyType',
  'Bidding.BroadBid': 'SearchBid',
  FormattedMedium: 'Medium',
  ID: 'AdGroupId',
  TrackingTemplateJson: 'TrackingTemplate',
  UrlCustomParametersJson: 'CustomParameters',
  ClickCount: 'Clicks',
  ImpressionCount: 'Impressions',
  ClickThrough: 'CTR',
  TotalSpendAmount: 'Spend',
  TotalCost: 'ClickAndPhoneSpend',
  ConversionCount: 'Conversions',
  AdGroupCallTrackingStatus: 'CallForwarding',
  PhoneCost: 'PhoneSpend',
  PhoneThroughRate: 'PTR',
};

export function getColumnsConfig(options) {
  return getBuilder(_.defaults({}, options.scope, _.pick(options, 'currentCampaign', 'globalCampaignTypeFilter')))
    .getColumnsConfig(options);
}

export function getColumnChooserConfig({
  scope, currentCampaign, filterService, isForVNext = false,
  globalCampaignTypeFilter,
}) {
  let customColumnConfig;

  if (Permissions.IsCustomColumnsInAdGroupsSupported) {
    const { metricCategories, segmentCategories } = adGroupMetricCategoriesFactory(Permissions);

    customColumnConfig = {
      enabled: true,
      metricCategories,
      segmentCategories,
      filterService,
    };
  }

  const bidColumns = [
    'SearchBid',
    'CpvBid',
    'CpmBid',
    'PercentCpcBid',
    'CommissionRate',
  ];
  const lockedColumns = getLockedBidColumns({
    campaignId: currentCampaign && currentCampaign.Id,
    currentCampaign,
    allBidColumns: bidColumns,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    globalCampaignTypeFilter,
  });
  const columnProcessorBeforeSetRecord = columnProcessorBeforeSetRecordGetter(lockedColumns);

  // eslint-disable-next-line max-len
  return _.defaults(getBuilder(_.defaults({}, scope, { currentCampaign, globalCampaignTypeFilter })).getColumnChooserConfig({
    i18n,
    patchQuery: true,
    Columns,
    SelectedColumns,
    LegacyColumns,
    LegacySelectedColumns,
    LegacyGridFlag,
    nameFromLegacy,
    CustomColumns,
    customColumnConfig,
    permissions: Permissions,
  }), {
    isForVNext,
    i18nErrors,
    columnProcessorBeforeSetRecord,
    AdCustomizerAttribute: jsData.AdCustomizerAttribute,
    resource: jsData.AdGroupAdCustomizerAttribute,
    RSAAdCustomizerAttributeColumn,
    enableRSAAdCustomizer: true,
  });
}
